import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Flex, Image, Link, Button, ButtonGroup, Text } from "@chakra-ui/react";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import logo from "../../assets/logo/mp_logo_white.png";
import bobWordmark from "../../assets/logo/bob/bob-wordmark.svg";
import { BobContent, BobLang } from "../../data/bobContent";

type BobHeaderProps = {
  lang: BobLang;
  onLangChange: (lang: BobLang) => void;
  ctaLabel: string;
  nav: BobContent["nav"];
};

const scrollToContact = () => {
  document.getElementById("bob-contact")?.scrollIntoView({ behavior: "smooth" });
};

const BobHeader = ({ lang, onLangChange, ctaLabel, nav }: BobHeaderProps) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={100}
      bg={scrolled ? "rgba(249, 250, 245, 0.85)" : "transparent"}
      backdropFilter={scrolled ? "blur(12px)" : "none"}
      borderBottom="1px solid"
      borderColor={scrolled ? "chalk.400" : "transparent"}
      boxShadow={scrolled ? "0 4px 20px rgba(20, 37, 26, 0.05)" : "none"}
      transition="background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease"
    >
      <Box width="100%" maxW="1400px" mx="auto" px={{ base: 5, md: 8 }} py={4}>
        <Flex justify="space-between" align="center" gap={4}>
          <Flex align="center" gap={{ base: 2, sm: 3 }} minW={0}>
            <Link
              as={RouterLink}
              to="/"
              flexShrink={0}
              display="flex"
              alignItems="center"
              gap={1}
              color="slate.500"
              _hover={{ color: "ink.900", textDecoration: "none" }}
              transition="color 0.2s ease"
            >
              <ChevronLeftIcon boxSize={4} />
              <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="medium" display={{ base: "none", sm: "block" }}>
                {nav.backLabel}
              </Text>
            </Link>
            <Link
              as={RouterLink}
              to="/"
              flexShrink={0}
              _hover={{ opacity: 0.8 }}
              transition="opacity 0.2s ease"
            >
              <Image
                w={{ base: "80px", md: "110px" }}
                src={logo}
                alt="Miller Productions, vissza a főoldalra"
                filter="brightness(0)"
                opacity={0.55}
              />
            </Link>
            <Box w="1px" h="20px" bg="chalk.500" flexShrink={0} display={{ base: "none", sm: "block" }} />
            <Link as={RouterLink} to="/bob" flexShrink={0}>
              <Image w={{ base: "52px", md: "64px" }} src={bobWordmark} alt="bob" />
            </Link>
          </Flex>

          <Flex align="center" gap={3}>
            <ButtonGroup size="xs" isAttached variant="outline" borderColor="chalk.500">
              <Button
                onClick={() => onLangChange("hu")}
                aria-pressed={lang === "hu"}
                bg={lang === "hu" ? "bob.900" : "transparent"}
                color={lang === "hu" ? "chalk.50" : "ink.900"}
                _hover={{ bg: lang === "hu" ? "bob.800" : "chalk.200" }}
              >
                HU
              </Button>
              <Button
                onClick={() => onLangChange("en")}
                aria-pressed={lang === "en"}
                bg={lang === "en" ? "bob.900" : "transparent"}
                color={lang === "en" ? "chalk.50" : "ink.900"}
                _hover={{ bg: lang === "en" ? "bob.800" : "chalk.200" }}
              >
                EN
              </Button>
            </ButtonGroup>

            <Button
              size="sm"
              bg="bob.300"
              color="bob.900"
              fontWeight="bold"
              _hover={{ bg: "bob.200" }}
              onClick={scrollToContact}
              display={{ base: "none", sm: "inline-flex" }}
            >
              {ctaLabel}
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default BobHeader;
